import { Box, Typography } from '@mui/material';
import { toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import { useUser } from '@sophocles/app';
import { formatDateTime, formatTime, formatDate } from '@sophocles/utils/formatUtils';
import { getUnitAbbreviation } from '@sophocles/utils/measurementUtils';

function MeasurementChart({
  list,
  measurementType,
  availableTypes,
  expectedReadings = {},
  displayRelays = [],
  relayInfo = {},
  displayUnit,
  groupMode = 'all',
  timeZone,
}) {
  const { t } = useTranslation();
  const { user } = useUser();

  if (!measurementType || measurementType === 'all' || !list || list.length === 0) {
    return (
      <Box sx={{ mb: 3, p: 2, bgcolor: 'var(--surface-secondary)', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          {t('measurements.chart')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {!list || list.length === 0 ? t('measurements.noDataInRange') : t('measurements.selectTypeForChart')}
        </Typography>
      </Box>
    );
  }

  // Build mapping from primary relay number → display relay label
  // Only map the primary (first) relay number — secondary relay data is ignored
  // because the primary relay's value already represents the combined circuit
  const relayNumToDisplayLabel = {};
  const primaryRelayNumbers = new Set();
  displayRelays.forEach(dr => {
    const primaryRn = dr.relayNumbers[0];
    relayNumToDisplayLabel[primaryRn] = dr.label;
    primaryRelayNumbers.add(primaryRn);
  });

  // Group measurements by timestamp for chart display
  const groupedData = {};
  // Track target_voltage per relay label for tooltip display (use primary relay only)
  const relayVoltages = {};

  list.forEach(measurement => {
    const isoTimestamp = measurement.datetime_timestamp || new Date(measurement.timestamp).toISOString();
    const timestampMs = new Date(isoTimestamp).getTime();

    if (!groupedData[timestampMs]) {
      groupedData[timestampMs] = {
        timestamp: timestampMs,
        displayTime: timeZone
          ? formatDateTime(toZonedTime(isoTimestamp, timeZone), user)
          : formatDateTime(isoTimestamp, user),
      };
    }

    // Skip secondary relay data — primary relay value represents the combined circuit
    const relayNum = measurement.relay?.relay_number;
    if (relayNum && !primaryRelayNumbers.has(relayNum)) return;

    const relayLabel =
      relayNum && relayNumToDisplayLabel[relayNum]
        ? relayNumToDisplayLabel[relayNum]
        : measurement.relay
          ? measurement.relay.name
          : `Relay ${measurement.discriminator || 'Unknown'}`;

    const raw = parseFloat(measurement.value) || 0;
    const value = measurement.unit === 'amps' ? Math.round(raw * 1000) / 1000 : Math.round(raw * 100) / 100;
    groupedData[timestampMs][relayLabel] = value;

    // Track target_voltage from primary relay only (first relay in paired group)
    if (measurement.target_voltage != null && !relayVoltages[relayLabel]) {
      relayVoltages[relayLabel] = measurement.target_voltage;
    }
  });

  // Transform data for chart
  let chartData = Object.values(groupedData).sort((a, b) => a.timestamp - b.timestamp);

  // When groupMode is byDay, aggregate chart data by day (max per relay per day)
  if (groupMode === 'byDay' && chartData.length > 0) {
    const allKeys = [
      ...new Set(
        chartData.flatMap(point => Object.keys(point).filter(key => key !== 'timestamp' && key !== 'displayTime')),
      ),
    ];
    const dayBuckets = {};
    chartData.forEach(point => {
      const d = new Date(point.timestamp);
      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!dayBuckets[dayKey]) {
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        dayBuckets[dayKey] = { timestamp: dayStart, displayTime: dayKey };
      }
      allKeys.forEach(key => {
        const val = point[key];
        if (typeof val === 'number') {
          dayBuckets[dayKey][key] = dayBuckets[dayKey][key] != null ? Math.max(dayBuckets[dayKey][key], val) : val;
        }
      });
    });
    chartData = Object.values(dayBuckets).sort((a, b) => a.timestamp - b.timestamp);
  }

  // Get measurement keys across ALL data points (not just the first — some relays may start reporting later)
  // Sort by displayRelays order so legend shows Relay 1, Relay 2, etc.
  const displayOrder = displayRelays.map(dr => dr.label);
  const measurementKeys = [
    ...new Set(
      chartData.flatMap(point => Object.keys(point).filter(key => key !== 'timestamp' && key !== 'displayTime')),
    ),
  ].sort((a, b) => {
    const ai = displayOrder.indexOf(a);
    const bi = displayOrder.indexOf(b);
    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
  });
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];

  // Calculate Y-axis max to include expected power reference lines
  const maxDataValue = chartData.reduce((max, point) => {
    measurementKeys.forEach(key => {
      const val = point[key];
      if (typeof val === 'number' && val > max) max = val;
    });
    return max;
  }, 0);
  const maxExpected =
    displayUnit !== 'current'
      ? displayRelays.reduce((max, dr) => {
          // Only include in Y-axis scaling if power measurement is active for all relays in group
          const allPowerActive = dr.relayNumbers.every(rn => relayInfo[rn]?.powerMeasurementActive !== false);
          if (!allPowerActive) return max;
          const val = expectedReadings[dr.key];
          return val && val > max ? val : max;
        }, 0)
      : 0;
  const yAxisMax = Math.ceil(Math.max(maxDataValue, maxExpected) * 1.05);

  // Calculate domain for time axis
  const timestamps = chartData.map(d => d.timestamp);
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  const timeSpan = maxTime - minTime;

  // Determine if we're looking at same day or multiple days
  const minDate = new Date(minTime);
  const maxDate = new Date(maxTime);
  const isSameDay = groupMode !== 'byDay' && minDate.toDateString() === maxDate.toDateString();

  // Generate explicit tick values for clean, uniform labels
  const generateTicks = () => {
    const ticks = [];
    if (groupMode === 'byDay') {
      // byDay mode: one tick per data point (each is a day)
      chartData.forEach(point => ticks.push(point.timestamp));
    } else if (isSameDay) {
      // Same day: ticks every 1-3 hours depending on span
      const hours = timeSpan / (1000 * 60 * 60);
      const interval = hours <= 3 ? 1 : hours <= 8 ? 2 : 3;
      const startHour = new Date(minTime);
      startHour.setMinutes(0, 0, 0);
      for (let t = startHour.getTime(); t <= maxTime; t += interval * 60 * 60 * 1000) {
        if (t >= minTime) ticks.push(t);
      }
    } else {
      // Multi-day: one tick per day at start of day
      const start = new Date(minTime);
      start.setHours(0, 0, 0, 0);
      const dayMs = 24 * 60 * 60 * 1000;
      const totalDays = Math.ceil(timeSpan / dayMs);
      // Skip days if too many to avoid crowding
      const step = totalDays > 14 ? Math.ceil(totalDays / 10) : 1;
      for (let t = start.getTime(); t <= maxTime + dayMs; t += step * dayMs) {
        ticks.push(t);
      }
    }
    return ticks;
  };

  const axisTicks = generateTicks();

  // Custom tick formatter for time axis - keep labels short
  const formatXAxis = tickItem => {
    const date = toZonedTime(tickItem, timeZone || 'UTC');
    if (groupMode === 'byDay' || !isSameDay) {
      return formatDate(date, user);
    }
    return formatTime(date, user);
  };

  // Determine Y-axis label based on measurement type and data
  let yAxisLabel = t('measurements.value');
  if (list.length > 0) {
    const firstMeasurement = list[0];
    if (firstMeasurement.unit) {
      yAxisLabel = getUnitAbbreviation(firstMeasurement.unit);
    }
  }

  // Custom tooltip that shows target_voltage when present
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <Box
        sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, boxShadow: 2 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {payload[0].payload.displayTime}
        </Typography>
        {payload.map(entry => {
          const voltage = relayVoltages[entry.name];
          return (
            <Typography key={entry.name} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.value} {yAxisLabel}
              {voltage != null ? ` (${voltage}V)` : ''}
            </Typography>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 1, width: '100%' }}>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--divider-color)" />
          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={[minTime, maxTime]}
            ticks={axisTicks}
            tickFormatter={formatXAxis}
            angle={-45}
            textAnchor="end"
            height={50}
            tick={{ fontSize: 10, fill: 'var(--text-secondary)' }}
            axisLine={{ stroke: 'var(--divider-color)' }}
          />
          <YAxis
            domain={[0, yAxisMax]}
            tick={{ fill: 'var(--text-secondary)' }}
            axisLine={{ stroke: 'var(--divider-color)' }}
            label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {measurementKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          ))}
          {expectedReadings &&
            displayUnit !== 'current' &&
            displayRelays.map((dr, index) => {
              const value = expectedReadings[dr.key];
              if (!value) return null;
              // Skip expected line if any relay in this group has power measurement off
              const allPowerActive = dr.relayNumbers.every(rn => relayInfo[rn]?.powerMeasurementActive !== false);
              if (!allPowerActive) return null;
              const color = colors[index % colors.length];
              return (
                <ReferenceLine
                  key={`expected-${dr.key}`}
                  y={value}
                  stroke={color}
                  strokeDasharray="5 5"
                  strokeOpacity={0.6}
                  label={{
                    value: t('measurements.expectedWatts', { value }),
                    position: 'insideTopRight',
                    fontSize: 11,
                    fill: color,
                  }}
                />
              );
            })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default MeasurementChart;
